import React, { CSSProperties, useMemo } from 'react'

import { BoxProps } from '@mui/material'
import { keyframes, SxProps, Theme } from '@mui/system'

import Box from 'components_new/atoms/Box'

export interface DriftingParticlesProps
  extends Omit<BoxProps, 'color' | 'height'> {
  count?: number
  height?: number
  color?: string
  minSize?: number
  maxSize?: number
  minDuration?: number
  maxDuration?: number
  seed?: number
  variant?: 'hover' | 'up'
}

type ParticleStyle = Pick<
  CSSProperties,
  'left' | 'top' | 'width' | 'height'
> & {
  animation: string
}

const hover = keyframes`
  0%   { transform: translate(0, 0); opacity: 0.35; }
  25%  { transform: translate(2px, -4px); opacity: 0.9; }
  50%  { transform: translate(0, -7px); opacity: 0.6; }
  75%  { transform: translate(-2px, -3px); opacity: 0.9; }
  100% { transform: translate(0, 0); opacity: 0.35; }
`

function mulberry32(seed: number): () => number {
  let a = seed

  return function () {
    a |= 0
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)

    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t

    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

const DriftingParticles = ({
  count = 14,
  height = 200,
  color = 'brand.main',
  minSize = 3,
  maxSize = 5,
  minDuration = 2.6,
  maxDuration = 5,
  seed = 20260902,
  variant = 'up',
  sx,
  ...rest
}: DriftingParticlesProps) => {
  const travel = height + 8

  const drift = useMemo(
    () => keyframes`
      0%   { transform: translateY(0); opacity: 0; }
      12%  { opacity: 0.9; }
      80%  { opacity: 0.9; }
      100% { transform: translateY(-${travel}px); opacity: 0; }
    `,
    [travel]
  )

  const particles = useMemo<ParticleStyle[]>(() => {
    const rand = mulberry32(seed)

    return Array.from({ length: count }, () => {
      const size = Math.round(minSize + rand() * (maxSize - minSize))
      const duration = (
        minDuration +
        rand() * (maxDuration - minDuration)
      ).toFixed(2)
      const delay = (rand() * maxDuration).toFixed(2)
      const left = `${(rand() * 96).toFixed(2)}%`

      if (variant === 'hover') {
        return {
          borderRadius: 1,
          left,
          top: `${(8 + rand() * 80).toFixed(2)}%`,
          width: size,
          height: size,
          // Negative delay starts each particle mid-cycle, so they don't all
          // fade in together on mount.
          animation: `${hover} ${(Number(duration) * 1.5).toFixed(2)}s ease-in-out infinite -${delay}s`
        }
      }

      return {
        borderRadius: 1,
        left,
        width: size,
        height: size,
        animation: `${drift} ${duration}s linear infinite ${delay}s`
      }
    })
  }, [count, seed, minSize, maxSize, minDuration, maxDuration, drift, variant])

  const base: SxProps<Theme> = {
    position: 'relative',
    height,
    overflow: 'hidden',
    color,
    '& > span': {
      position: 'absolute',
      ...(variant === 'up' && { bottom: '-8px' }),
      borderRadius: '1px',
      bgcolor: 'currentColor',
      opacity: 0,
      willChange: 'transform, opacity'
    },
    '@media (prefers-reduced-motion: reduce)': {
      '& > span': {
        animation: 'none',
        opacity: 0.35,
        transform:
          variant === 'up' ? `translateY(-${Math.round(travel / 2)}px)` : 'none'
      }
    }
  }

  return (
    <Box
      aria-hidden="true"
      sx={[
        base,
        ...[
          {
            background: 'linear-gradient(transparent, rgba(34, 197, 94, 0.12))'
          }
        ],
        ...(Array.isArray(sx) ? sx : [sx])
      ]}
      {...rest}
    >
      {particles.map((p, i) => (
        <Box key={i} component="span" sx={p} />
      ))}
    </Box>
  )
}

export default DriftingParticles
